<template>
  <section class="comm-prefs d-flex flex-column p-2 p-lg-0">
    <h2 class="title-2 text-primary-black mb-3">
      {{ t('profile.communicationPreferences.title') }}
    </h2>

    <p v-if="loading" class="text-grey-700">
      {{ t('profile.communicationPreferences.loading') }}
    </p>

    <template v-else>
      <div class="prefs-body">
        <section class="prefs-section">
          <h3 class="prefs-section__title">
            {{ t('profile.communicationPreferences.sectionDealsTitle') }}
          </h3>
          <p class="prefs-section__intro">
            {{ t('profile.communicationPreferences.sectionDealsIntro') }}
          </p>

          <div class="prefs-row">
            <div class="prefs-row__text">
              <span class="prefs-row__label">{{
                t('profile.communicationPreferences.switchEmail')
              }}</span>
              <span class="prefs-row__desc">{{
                t('profile.communicationPreferences.switchEmailDesc')
              }}</span>
            </div>
            <ToggleSwitch
              :active="draft.email"
              :label="t('profile.communicationPreferences.switchEmail')"
              @toggle="toggleEmail"
            />
          </div>

          <div class="prefs-row">
            <div class="prefs-row__text">
              <span class="prefs-row__label">{{
                t('profile.communicationPreferences.switchSms')
              }}</span>
              <span class="prefs-row__desc">{{
                t('profile.communicationPreferences.switchSmsDesc')
              }}</span>
            </div>
            <ToggleSwitch
              :active="draft.sms"
              :label="t('profile.communicationPreferences.switchSms')"
              @toggle="draft.sms = !draft.sms"
            />
          </div>
        </section>

        <hr class="prefs-separator" />

        <section class="prefs-section">
          <h3 class="prefs-section__title">
            {{ t('profile.communicationPreferences.sectionPartnersTitle') }}
          </h3>
          <p
            class="prefs-section__intro"
            v-html="t('profile.communicationPreferences.sectionPartnersIntro')"
          />

          <div class="prefs-row prefs-row--start">
            <div class="prefs-row__text">
              <span class="prefs-row__label">{{
                t('profile.communicationPreferences.switchPartenaires')
              }}</span>
              <span
                class="prefs-row__desc"
                v-html="t('profile.communicationPreferences.switchPartenairesDesc')"
              />
            </div>
            <ToggleSwitch
              :active="draft.partenaires"
              :label="t('profile.communicationPreferences.switchPartenaires')"
              @toggle="draft.partenaires = !draft.partenaires"
            />
          </div>
        </section>

        <hr class="prefs-separator" />

        <section class="prefs-section">
          <h3 class="prefs-section__title">
            {{ t('profile.communicationPreferences.sectionTrackingTitle') }}
          </h3>
          <p class="prefs-section__intro">
            {{ t('profile.communicationPreferences.sectionTrackingIntro') }}
          </p>

          <div class="prefs-row prefs-row--start">
            <div class="prefs-row__text">
              <span class="prefs-row__label">{{
                t('profile.communicationPreferences.switchTracking')
              }}</span>
              <span class="prefs-row__desc">{{
                t('profile.communicationPreferences.switchTrackingDesc')
              }}</span>
            </div>
            <ToggleSwitch
              :active="draft.trackingOpen"
              :label="t('profile.communicationPreferences.switchTracking')"
              @toggle="draft.trackingOpen = !draft.trackingOpen"
            />
          </div>
        </section>
      </div>

      <div class="comm-prefs__footer">
        <button
          type="button"
          class="comm-prefs__back"
          :disabled="saving"
          @click="handleBack"
        >
          {{ t('profile.communicationPreferences.back') }}
        </button>
        <div class="comm-prefs__save-wrap">
          <Button
            id="comm-prefs-save"
            class="comm-prefs__save"
            :classes="['button-primary', 'bg-primary-blue', 'comm-prefs__save-btn']"
            :disabled="saving"
            :loading="saving"
            @buttonClick="handleSave"
          >
            <template #text>
              {{ t('profile.communicationPreferences.save') }}
            </template>
          </Button>
        </div>
      </div>
    </template>
  </section>
</template>

<script lang="ts" setup>
  import { computed, onMounted, reactive, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import ToggleSwitch from '@/components/common/ToggleSwitch/v1/ToggleSwitch.vue'
  import Button from '@/components/UI/Button.vue'
  import loggedInUser from '@/state/loggedInUser'
  import isSubscribeNewsletter from '@/state/isSubscribeNewsletter'
  import {
    loadCdaConsentFromCrm,
    saveCdaConsent,
  } from '@/logic/contact/cdaConsent'
  import { CdaConsent, defaultCdaConsent } from '@/types/CdaConsent'
  import { useStore } from '@/store'

  const emit = defineEmits<{
    (e: 'close'): void
  }>()

  const { t } = useI18n()
  const store = useStore()
  const loading = ref(true)
  const saving = ref(false)
  const draft = reactive<CdaConsent>(defaultCdaConsent())
  /** Snapshot when panel opened — used to detect email turned off (clear IL). */
  const loadedEmail = ref(false)

  const userEmail = computed(
    () => store.state.auth?.user?.email || loggedInUser.value?.email
  )

  const syncDraftFromConsent = () => {
    Object.assign(draft, { ...isSubscribeNewsletter.value, whatsapp: false })
    loadedEmail.value = !!isSubscribeNewsletter.value.email
  }

  const toggleEmail = () => {
    const enabled = !draft.email
    draft.email = enabled
    draft.trackingOpen = enabled
  }

  const handleSave = async () => {
    if (saving.value) return
    saving.value = true
    try {
      const next: CdaConsent = { ...draft, whatsapp: false }
      const clearLegitimateInterest = loadedEmail.value && !next.email
      await saveCdaConsent(
        userEmail.value,
        next,
        clearLegitimateInterest ? { clearLegitimateInterest: true } : undefined
      )
      loadedEmail.value = next.email
      emit('close')
    } finally {
      saving.value = false
    }
  }

  const handleBack = () => {
    syncDraftFromConsent()
    emit('close')
  }

  onMounted(async () => {
    loading.value = true
    try {
      if (userEmail.value) {
        await loadCdaConsentFromCrm(userEmail.value)
      }
      syncDraftFromConsent()
    } finally {
      loading.value = false
    }
  })
</script>

<style scoped lang="scss">
  .comm-prefs {
    min-height: 100%;
  }

  .prefs-body {
    flex: 1;
  }

  .prefs-section {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
  }

  .prefs-section__title {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 800;
    color: #0d141b;
    line-height: 1.3;
  }

  .prefs-section__intro {
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.35;
    color: #585f66;

    :deep(a) {
      color: inherit;
      font-weight: 700;
      text-decoration: underline;
      cursor: pointer;
    }
  }

  .prefs-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;

    &--start {
      align-items: flex-start;
    }
  }

  .prefs-row__text {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    min-width: 0;
    flex: 1;
  }

  .prefs-row__label {
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.5;
    color: #0d141b;
  }

  .prefs-row__desc {
    font-size: 0.875rem;
    line-height: 1.35;
    color: #585f66;

    :deep(a) {
      color: inherit;
      font-weight: 700;
      text-decoration: underline;
      cursor: pointer;
    }
  }

  .prefs-separator {
    margin: 1.25rem 0;
    border: 0;
    border-top: 1px solid #dee2e6;
  }

  .comm-prefs__footer {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    margin-top: 1.5rem;
    padding-top: 0.5rem;
  }

  .comm-prefs__save-wrap {
    width: 100%;
    order: -1;
  }

  .comm-prefs__save {
    width: 100%;
  }

  :deep(.comm-prefs__save-btn) {
    width: 100%;
    min-height: 48px;
    border-radius: 999px;
    border: none;
  }

  .comm-prefs__back {
    align-self: center;
    padding: 0;
    border: 0;
    background: none;
    color: var(--primary-blue, #0042b8);
    font-size: 0.875rem;
    font-weight: 700;
    text-decoration: underline;
    cursor: pointer;
    text-align: center;
  }

  .comm-prefs__back:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (min-width: 992px) {
    .comm-prefs__footer {
      flex-direction: row;
      align-items: center;
      justify-content: flex-end;
      gap: 1.25rem;
    }

    .comm-prefs__back {
      display: none;
    }

    .comm-prefs__save-wrap {
      order: 0;
      width: auto;
      margin-left: 0;
    }

    .comm-prefs__save {
      width: auto;
      min-width: 200px;
    }

    :deep(.comm-prefs__save-btn) {
      width: auto;
      min-width: 200px;
      padding-left: 2rem;
      padding-right: 2rem;
    }
  }
</style>
